import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import * as THREE from 'three';
import { Temoignage } from '../models/temoignage.model';
import { TemoignageService } from '../services/temoignage.service';
import { Parametre } from '../models/parametre.model';
import { ParametreService } from '../services/parametre.service';
import { Reclamation } from '../models/reclamation.model';
import { ReclamationService } from '../services/reclamation.service';

@Component({
  selector: 'app-accueil',
  templateUrl: './accueil.component.html',
  styleUrls: ['./accueil.component.css']
})
export class AccueilComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('canvasGlobe') canvasRef!: ElementRef<HTMLCanvasElement>;

  temoignages: Temoignage[] = [];
  parametres: Parametre | null = null;

  // Bascule entre "globe visible" et "photo visible" toutes les quelques secondes
  photoVisible = false;

  // Statistiques affichees dans la section "L'ame du festival", animees au chargement
  statNuits = 0;
  statArtistes = 0;
  statSpectateurs = 0;

  // Formulaire "Avis & reclamations"
  formAvis: Reclamation = { nom: '', email: '', type: 'avis', sujet: '', message: '' };
  avisEnvoye = false;
  envoiEnCours = false;

  // Formulaire public de soumission d'un temoignage (sera modere par l'admin avant publication)
  formTemoignage: Temoignage = { auteur: '', lieu: '', contenu: '', note: 5, statut: 'attente' };
  temoignageEnvoye = false;
  envoiTemoignageEnCours = false;

  // ---------- Minuteur a rebours avant l'ouverture du festival ----------
  joursAvantOuverture = '00';
  heuresAvantOuverture = '00';
  minutesAvantOuverture = '00';
  secondesAvantOuverture = '00';
  festivalDejaOuvert = false;
  private minuteurCompteARebours: any;

  constructor(
    private temoignageService: TemoignageService,
    private parametreService: ParametreService,
    private reclamationService: ReclamationService
  ) { }

  ngOnInit(): void {
    this.temoignageService.getApprouves().subscribe(data => {
      this.temoignages = data;
    });

    this.parametreService.getParametres().subscribe(data => {
      this.parametres = data;
      this.demarrerCompteARebours();
    });

    // Alterne entre le globe (affiche en premier) et la photo de Sousse
    setInterval(() => {
      this.photoVisible = !this.photoVisible;
    }, 4000);

    this.animerStatistiques();
  }

  // Fait monter les chiffres de 0 jusqu'a leur valeur finale, avec un effet progressif
  animerStatistiques(): void {
    this.animerUnCompteur(15, (v) => this.statNuits = v);
    this.animerUnCompteur(40, (v) => this.statArtistes = v);
    this.animerUnCompteur(120, (v) => this.statSpectateurs = v);
  }

  private animerUnCompteur(cible: number, appliquer: (valeur: number) => void): void {
    const duree = 1400;
    const debut = performance.now();

    const etape = (maintenant: number) => {
      const progres = Math.min(1, (maintenant - debut) / duree);
      const valeurAdoucie = 1 - Math.pow(1 - progres, 3);
      appliquer(Math.round(cible * valeurAdoucie));
      if (progres < 1) requestAnimationFrame(etape);
    };
    requestAnimationFrame(etape);
  }

  envoyerAvis(): void {
    if (!this.formAvis.nom || !this.formAvis.email || !this.formAvis.message) return;
    this.envoiEnCours = true;
    this.reclamationService.envoyer(this.formAvis).subscribe({
      next: () => {
        this.envoiEnCours = false;
        this.avisEnvoye = true;
      },
      error: () => {
        this.envoiEnCours = false;
        alert("Impossible d'envoyer votre message pour le moment. Réessayez plus tard.");
      }
    });
  }

  envoyerTemoignage(): void {
    if (!this.formTemoignage.auteur || !this.formTemoignage.lieu || !this.formTemoignage.contenu) return;
    this.envoiTemoignageEnCours = true;
    this.temoignageService.creer(this.formTemoignage).subscribe({
      next: () => {
        this.envoiTemoignageEnCours = false;
        this.temoignageEnvoye = true;
      },
      error: () => {
        this.envoiTemoignageEnCours = false;
        alert("Impossible d'envoyer votre témoignage pour le moment. Réessayez plus tard.");
      }
    });
  }

  // ---------- Minuteur a rebours ----------
  demarrerCompteARebours(): void {
    this.calculerCompteARebours();
    this.minuteurCompteARebours = setInterval(() => this.calculerCompteARebours(), 1000);
  }

  private calculerCompteARebours(): void {
    const valeurSaisie = this.parametres?.dateOuverture?.trim();
    let cible = valeurSaisie ? new Date(valeurSaisie).getTime() : NaN;

    // Si aucune date valide n'est configuree dans Admin -> Parametres, on utilise une
    // valeur de secours toujours dans le futur (60 jours a partir d'aujourd'hui), plutot
    // qu'une date fixe qui finit par se retrouver dans le passe avec le temps.
    if (isNaN(cible)) {
      cible = Date.now() + 60 * 24 * 60 * 60 * 1000;
    }

    const maintenant = Date.now();
    const restant = cible - maintenant;

    if (restant <= 0) {
      this.festivalDejaOuvert = true;
      this.joursAvantOuverture = '00';
      this.heuresAvantOuverture = '00';
      this.minutesAvantOuverture = '00';
      this.secondesAvantOuverture = '00';
      return;
    }

    this.festivalDejaOuvert = false;
    const deuxChiffres = (n: number) => n.toString().padStart(2, '0');

    this.joursAvantOuverture = deuxChiffres(Math.floor(restant / (1000 * 60 * 60 * 24)));
    this.heuresAvantOuverture = deuxChiffres(Math.floor((restant / (1000 * 60 * 60)) % 24));
    this.minutesAvantOuverture = deuxChiffres(Math.floor((restant / (1000 * 60)) % 60));
    this.secondesAvantOuverture = deuxChiffres(Math.floor((restant / 1000) % 60));
  }

  ngOnDestroy(): void {
    if (this.minuteurCompteARebours) {
      clearInterval(this.minuteurCompteARebours);
    }
  }

  ngAfterViewInit(): void {
    this.initGlobe();
  }

  initGlobe(): void {
    const canvas = this.canvasRef.nativeElement;
    const conteneur = canvas.parentElement;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
    camera.position.set(0, 0.25, 6.8);

    // Redimensionne le rendu pour occuper toute la largeur disponible (le globe
    // couvre desormais toute la moitie gauche du hero, pas un petit cercle fixe)
    const redimensionner = () => {
      const taille = conteneur?.clientWidth || 480;
      const hauteur = conteneur?.clientHeight || taille;
      renderer.setSize(taille, hauteur, false);
      camera.aspect = taille / hauteur;
      camera.updateProjectionMatrix();
    };
    redimensionner();
    window.addEventListener('resize', redimensionner);

    const groupe = new THREE.Group();
    groupe.rotation.set(0.25, -2.1, 0);
    scene.add(groupe);

    // Champ d'etoiles en arriere-plan pour un rendu plus realiste / spatial
    const etoilesGeo = new THREE.BufferGeometry();
    const nbEtoiles = 600;
    const positions = new Float32Array(nbEtoiles * 3);
    for (let i = 0; i < nbEtoiles; i++) {
      const rayon = 20 + Math.random() * 30;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i * 3] = rayon * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = rayon * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = rayon * Math.cos(phi);
    }
    etoilesGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const etoiles = new THREE.Points(etoilesGeo, new THREE.PointsMaterial({ color: 0xf4eee0, size: 0.05, transparent: true, opacity: 0.55 }));
    scene.add(etoiles);

    // Texture du globe generee directement dans le navigateur (aucune image telechargee,
    // donc aucun risque de blocage reseau/404) : ocean marine + continents stylises dores,
    // dans les couleurs du site plutot qu'une photo satellite realiste.
    const texture = this.creerTextureGlobeStylisee();

    const materiauTerre = new THREE.MeshStandardMaterial({ map: texture, roughness: 0.7, metalness: 0.1 });

    const terre = new THREE.Mesh(new THREE.SphereGeometry(2.6, 64, 48), materiauTerre);
    groupe.add(terre);


    // Couche de nuages, legerement plus grande que le globe, tourne plus vite -> effet vivant.
    // Texture generee elle aussi dans le navigateur (memes raisons que pour le globe).
    const nuages = new THREE.Mesh(
      new THREE.SphereGeometry(2.635, 64, 48),
      new THREE.MeshStandardMaterial({
        map: this.creerTextureNuagesStylisee(),
        transparent: true,
        opacity: 0.3,
        depthWrite: false
      })
    );
    groupe.add(nuages);

    // Halo d'atmosphere : sphere legerement plus grande, face arriere uniquement, effet Fresnel simplifie
    const atmosphere = new THREE.Mesh(
      new THREE.SphereGeometry(2.75, 48, 32),
      new THREE.MeshBasicMaterial({ color: 0x6fa8dc, transparent: true, opacity: 0.12, side: THREE.BackSide })
    );
    groupe.add(atmosphere);

    const grille = new THREE.Mesh(
      new THREE.SphereGeometry(2.63, 28, 16),
      new THREE.MeshBasicMaterial({ color: 0xc9a24b, wireframe: true, transparent: true, opacity: 0.06 })
    );
    groupe.add(grille);

    scene.add(new THREE.AmbientLight(0x8899bb, 0.9));
    const lumiere = new THREE.PointLight(0xfff2d6, 1.8, 26);
    lumiere.position.set(7, 4, 10);
    scene.add(lumiere);
    const lumiereAppoint = new THREE.PointLight(0x6fa8dc, 0.5, 30);
    lumiereAppoint.position.set(-8, -3, -6);
    scene.add(lumiereAppoint);

    function latLonVersVecteur(lat: number, lon: number, rayon: number): THREE.Vector3 {
      const phi = (90 - lat) * (Math.PI / 180);
      const theta = (lon + 180) * (Math.PI / 180);
      return new THREE.Vector3(
        rayon * Math.sin(phi) * Math.cos(theta),
        rayon * Math.cos(phi),
        rayon * Math.sin(phi) * Math.sin(theta)
      );
    }

    const sousse = latLonVersVecteur(35.83, 10.64, 2.62);
    const marqueur = new THREE.Mesh(
      new THREE.SphereGeometry(0.06, 16, 16),
      new THREE.MeshBasicMaterial({ color: 0xe0663f })
    );
    marqueur.position.copy(sousse);
    groupe.add(marqueur);

    // Halo pulsant autour du marqueur de Sousse pour bien le mettre en valeur
    const halo = new THREE.Mesh(
      new THREE.RingGeometry(0.09, 0.13, 32),
      new THREE.MeshBasicMaterial({ color: 0xe0663f, transparent: true, opacity: 0.7, side: THREE.DoubleSide })
    );
    halo.position.copy(sousse);
    halo.lookAt(sousse.clone().multiplyScalar(2));
    groupe.add(halo);

    const villes = [{ lat: 48.85, lon: 2.35 }, { lat: 41.9, lon: 12.5 }, { lat: 51.5, lon: -0.12 }];
    villes.forEach(v => {
      const destination = latLonVersVecteur(v.lat, v.lon, 2.62);
      const milieu = sousse.clone().add(destination).multiplyScalar(0.5).normalize().multiplyScalar(sousse.length() * 1.35);
      const courbe = new THREE.QuadraticBezierCurve3(sousse, milieu, destination);
      const points = courbe.getPoints(40);
      const geometrie = new THREE.BufferGeometry().setFromPoints(points);
      const materiau = new THREE.LineBasicMaterial({ color: 0xe9cd8c, transparent: true, opacity: 0.55 });
      groupe.add(new THREE.Line(geometrie, materiau));
    });

    let debutAnimation = performance.now();
    let animationArretee = false;

    function animer(maintenant: number) {
      if (animationArretee) return;
      requestAnimationFrame(animer);

      // Toute la logique est protegee : si une frame echoue pour une raison quelconque
      // (ressource pas encore prete, calcul invalide...), on ignore juste cette frame
      // au lieu de casser silencieusement toute la boucle d'animation - c'est ce qui
      // faisait "disparaitre" le globe definitivement jusqu'au rechargement de la page.
      try {
        groupe.rotation.y += 0.06;
        nuages.rotation.y += 0.025;
        const impulsion = 1 + Math.sin((maintenant - debutAnimation) / 500) * 0.15;
        halo.scale.setScalar(impulsion);
        renderer.render(scene, camera);
      } catch (erreur) {
        console.warn('Frame du globe ignorée :', erreur);
      }
    }
    requestAnimationFrame(animer);

    // Si le navigateur/GPU perd le contexte WebGL (bascule d'onglet prolongee, economie
    // d'energie, etc.), on relance proprement le rendu des qu'il est restaure, plutot
    // que de laisser un globe fige/vide en permanence.
    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      animationArretee = true;
    });
    canvas.addEventListener('webglcontextrestored', () => {
      animationArretee = false;
      redimensionner();
      requestAnimationFrame(animer);
    });

    // "Rejoue" l'arrivee sur l'accueil : remet le globe dans sa position de depart et
    // relance l'animation des statistiques a chaque fois que la section hero redevient
    // visible (ex. l'utilisateur etait alle voir une autre section puis remonte en haut).
    const sectionHero = document.getElementById('hero');
    if (sectionHero) {
      const observateur = new IntersectionObserver((entrees) => {
        entrees.forEach(entree => {
          if (entree.isIntersecting) {
            groupe.rotation.set(0.25, -2.1, 0);
            debutAnimation = performance.now();
            this.animerStatistiques();
          }
        });
      }, { threshold: 0.4 });
      observateur.observe(sectionHero);
    }
  }

  // Dessine un ocean stylise + continents simplifies (Afrique, Europe, Moyen-Orient)
  // directement sur un canvas, dans les couleurs du site (dore sur marine) plutot qu'une
  // photo satellite realiste. Aucune requete reseau : ca ne peut jamais echouer.
  private creerTextureGlobeStylisee(): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 512;
    const ctx = canvas.getContext('2d')!;

    const degrade = ctx.createLinearGradient(0, 0, 0, 512);
    degrade.addColorStop(0, '#0a1526');
    degrade.addColorStop(0.5, '#1c3a5e');
    degrade.addColorStop(1, '#0a1526');
    ctx.fillStyle = degrade;
    ctx.fillRect(0, 0, 1024, 512);

    const versXY = (lon: number, lat: number): [number, number] => [
      (lon + 180) / 360 * 1024,
      (90 - lat) / 180 * 512
    ];

    const dessinerBlob = (points: [number, number][]) => {
      ctx.beginPath();
      points.forEach(([lon, lat], i) => {
        const [x, y] = versXY(lon, lat);
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      ctx.closePath();
      ctx.fill();
    };

    ctx.fillStyle = '#c9a24b';
    ctx.globalAlpha = 0.8;

    // Formes tres simplifiees, pas geographiquement exactes - juste evocatrices
    dessinerBlob([[-17, 35], [10, 37], [30, 31], [43, 12], [40, -5], [35, -25], [20, -34], [12, -30], [10, -5], [-5, 5], [-17, 15]]); // Afrique
    dessinerBlob([[-9, 43], [2, 51], [15, 54], [30, 45], [26, 40], [12, 37], [0, 38]]); // Europe
    dessinerBlob([[35, 37], [50, 40], [60, 30], [50, 15], [40, 12], [36, 25]]); // Moyen-Orient

    ctx.globalAlpha = 1;

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    return texture;
  }

  // Quelques taches blanches semi-transparentes en guise de nuages stylises
  private creerTextureNuagesStylisee(): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 512;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, 1024, 512);
    ctx.fillStyle = '#ffffff';

    for (let i = 0; i < 45; i++) {
      const x = Math.random() * 1024;
      const y = Math.random() * 512;
      const rayonX = 30 + Math.random() * 60;
      const rayonY = rayonX * (0.3 + Math.random() * 0.3);
      ctx.globalAlpha = 0.08 + Math.random() * 0.12;
      ctx.beginPath();
      ctx.ellipse(x, y, rayonX, rayonY, Math.random() * Math.PI, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    return texture;
  }
}
